/**
 * CreateTokenScreen — deploy a standard eosio.token-style contract on your
 * own account, then create (+ optionally issue) a token in one transaction.
 * Mirrors blocksfinity.com's /wallet/createtoken.
 */

import { useEffect, useState } from 'react'
import type { Session } from '@wharfkit/session'
import {
  deployAndCreateToken,
  accountHasContract,
} from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'

export default function CreateTokenScreen() {
  const { t } = useTranslation()
  const { activeAccount, activeSession, goBack, navigate } = useWalletStore()

  const [contractAccount, setContractAccount] = useState(activeAccount?.name ?? '')
  const [hasContract, setHasContract] = useState<boolean | null>(null)
  const [checkingContract, setCheckingContract] = useState(false)

  const [symbol, setSymbol] = useState('')
  const [precision, setPrecision] = useState('4')
  const [maxSupplyAmount, setMaxSupplyAmount] = useState('1000000')
  const [issuer, setIssuer] = useState(activeAccount?.name ?? '')
  const [issueNow, setIssueNow] = useState(true)
  const [issueAmount, setIssueAmount] = useState('1000')
  const [issueMemo, setIssueMemo] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [txid, setTxid] = useState('')

  useEffect(() => {
    const acc = contractAccount.trim().toLowerCase()
    if (!acc) { setHasContract(null); return }
    setCheckingContract(true)
    const timer = setTimeout(() => {
      void accountHasContract(acc)
        .then(setHasContract)
        .finally(() => setCheckingContract(false))
    }, 400)
    return () => clearTimeout(timer)
  }, [contractAccount])

  const symbolUpper = symbol.trim().toUpperCase()
  const precisionNum = Math.max(0, Math.min(18, parseInt(precision, 10) || 0))
  const maxSupplyAsset = `${(parseFloat(maxSupplyAmount) || 0).toFixed(precisionNum)} ${symbolUpper}`
  const issueAsset = `${(parseFloat(issueAmount) || 0).toFixed(precisionNum)} ${symbolUpper}`

  const canSubmit =
    !!activeSession &&
    !!contractAccount.trim() &&
    /^[A-Z]{1,7}$/.test(symbolUpper) &&
    (parseFloat(maxSupplyAmount) || 0) > 0 &&
    !!issuer.trim() &&
    (!issueNow || (parseFloat(issueAmount) || 0) > 0) &&
    !busy

  const handleCreate = async () => {
    if (!activeSession) return
    setError('')
    setBusy(true)
    try {
      const session = activeSession as Session
      const signingPermission = session.permission.toString()
      const id = await deployAndCreateToken(session, {
        contractAccount: contractAccount.trim().toLowerCase(),
        skipDeploy: hasContract === true,
        maxSupply: maxSupplyAsset,
        issuer: issuer.trim().toLowerCase(),
        issueAmount: issueNow ? issueAsset : undefined,
        issueMemo,
        signingPermission,
      })
      setTxid(id)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (txid) {
    return (
      <div className="flex flex-col h-full bg-surface">
        <div className="bg-surface-1 pt-safe px-4 py-3 border-b border-surface-3 flex items-center gap-3 flex-shrink-0">
          <h2 className="text-white font-semibold flex-1 text-center">{t('createToken.title')}</h2>
        </div>
        <div className="scroll-area flex-1 px-4 py-6 space-y-4">
          <div className="card-highlighted text-center">
            <p className="text-white font-semibold text-lg">✓ {t('createToken.success')}</p>
            <p className="text-brand-light font-mono text-sm mt-1">{maxSupplyAsset}</p>
            <p className="text-gray-500 text-xs mt-2 break-all">{txid}</p>
          </div>
          <button className="btn-primary w-full" onClick={() => navigate('tools')}>
            {t('common.done')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-surface">
      <div className="bg-surface-1 pt-safe px-4 py-3 border-b border-surface-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={goBack} className="text-gray-400 text-sm">← {t('common.back')}</button>
        <h2 className="text-white font-semibold flex-1 text-center">{t('createToken.title')}</h2>
        <div className="w-16" />
      </div>

      <div className="scroll-area flex-1 px-4 py-4 space-y-4">
        <p className="text-xs text-gray-400">{t('createToken.intro')}</p>

        <div className="space-y-1.5">
          <label className="text-xs text-gray-400">{t('createToken.contractAccount')}</label>
          <input
            className="input-field font-mono"
            value={contractAccount}
            onChange={e => setContractAccount(e.target.value.toLowerCase())}
            maxLength={12}
          />
          <p className="text-xs text-gray-500">
            {checkingContract
              ? t('createToken.checkingContract')
              : hasContract
                ? t('createToken.alreadyDeployed')
                : t('createToken.contractAccountHint')}
          </p>
        </div>

        <div className="card space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs text-gray-400">{t('createToken.symbol')}</label>
              <input
                className="input-field font-mono"
                value={symbol}
                onChange={e => setSymbol(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                placeholder="MYTOKEN"
                maxLength={7}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-gray-400">{t('createToken.precision')}</label>
              <input
                type="number"
                className="input-field"
                value={precision}
                onChange={e => setPrecision(e.target.value)}
                min={0}
                max={18}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">{t('createToken.maxSupply')}</label>
            <input
              type="number"
              className="input-field"
              value={maxSupplyAmount}
              onChange={e => setMaxSupplyAmount(e.target.value)}
            />
            <p className="text-xs text-gray-500 font-mono">{maxSupplyAsset}</p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">{t('createToken.issuer')}</label>
            <input
              className="input-field font-mono"
              value={issuer}
              onChange={e => setIssuer(e.target.value.toLowerCase())}
              maxLength={12}
            />
          </div>
        </div>

        <div className="card space-y-3">
          <label className="flex items-center gap-2 text-sm text-white">
            <input type="checkbox" checked={issueNow} onChange={e => setIssueNow(e.target.checked)} />
            {t('createToken.issueNow')}
          </label>
          {issueNow && (
            <>
              <div className="space-y-1.5">
                <label className="text-xs text-gray-400">{t('createToken.issueAmount')}</label>
                <input
                  type="number"
                  className="input-field"
                  value={issueAmount}
                  onChange={e => setIssueAmount(e.target.value)}
                />
                <p className="text-xs text-gray-500 font-mono">{issueAsset}</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-gray-400">{t('createToken.issueMemo')}</label>
                <input
                  className="input-field"
                  value={issueMemo}
                  onChange={e => setIssueMemo(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <p className="text-danger text-xs">{t('createToken.warning')}</p>

        {error ? <p className="text-danger text-xs text-center">{error}</p> : null}

        <button className="btn-primary w-full" disabled={!canSubmit} onClick={handleCreate}>
          {busy ? t('createToken.creating') : t('createToken.create')}
        </button>
      </div>
    </div>
  )
}
